/**
 * The draft board itself: 160 slots, who owns each one, and who is allowed to type into it.
 *
 * Three things make this more than a grid.
 *
 * 1. OWNERSHIP ≠ SEATING. Slot 5 in round 9 belongs to whoever sits fifth, but the PICK may
 *    have been traded. The board tracks both, because "Rajan's 9th" is how people talk about it
 *    and "John is on the clock" is what has to be true on the screen.
 * 2. KEEPERS ARE ALREADY MADE. A declared keeper consumes a real pick before the draft starts.
 *    Preloading them is the single biggest thing the spreadsheet does not do and the reason
 *    draft night always begins with ten minutes of "wait, who has the 11th?".
 * 3. PERMISSION IS PER-PICK. Only the GM on the clock — or somebody he has asked to draft for
 *    him, which happens most years — can enter it. Commissioners can enter anything.
 */

export interface BoardPick {
  overall: number;
  round: number;
  /** 1-based seat, snake-adjusted: in even rounds slot 1 picks last. */
  slot: number;
  /** Whose seat this is. */
  seat: string;
  /** Who actually makes the pick, after trades. */
  owner: string;
  player?: string;
  pos?: string;
  team?: string;
  bye?: number | null;
  /** A keeper occupying the pick rather than a selection made on the night. */
  keeper?: boolean;
  /**
   * A keeper who came up through the farm system. The board draws him in a violet frame rather
   * than the keeper's near-black one, because a farm graduate's cost is computed a different
   * way (2013-III-1 §4) and "where did that one come from" is a question asked at the board.
   */
  farm?: boolean;
  madeAt?: string;
  madeBy?: string;
}

/**
 * Build the empty board from the first-round seating.
 *
 * `ownership` maps "round:seat" to the GM who now holds that pick. Anything absent stays with
 * the seat, which is the overwhelmingly common case.
 */
export function buildBoard(
  order: readonly string[],
  rounds: number,
  ownership: ReadonlyMap<string, string> = new Map(),
): BoardPick[] {
  const teams = order.length;
  const picks: BoardPick[] = [];
  let overall = 1;
  for (let round = 1; round <= rounds; round++) {
    const forward = round % 2 === 1;
    for (let i = 0; i < teams; i++) {
      const seatIndex = forward ? i : teams - 1 - i;
      const seat = order[seatIndex]!;
      picks.push({
        overall,
        round,
        slot: seatIndex + 1,
        seat,
        owner: ownership.get(`${round}:${seat}`) ?? seat,
      });
      overall++;
    }
  }
  return picks;
}

/**
 * Turn the per-team owned-pick lists the trade ledger produces into the ownership map
 * `buildBoard` wants. Each entry says "this GM holds a round-N pick that originally belonged to
 * `from`" — so the key is the ORIGINAL seat, which is what identifies the slot on the board.
 */
export function ownershipFrom(
  owned: Readonly<Record<string, ReadonlyArray<{ round: number; from: string }>>>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const [holder, picks] of Object.entries(owned)) {
    for (const p of picks) {
      if (p.from.toUpperCase() === holder.toUpperCase()) continue;
      map.set(`${p.round}:${p.from}`, holder);
    }
  }
  return map;
}

/** One pick, identified the way the board identifies it: round plus the seat it started in. */
export interface PickRef { round: number; seat: string }

/** A hypothetical swap: each side receives the picks listed against it. */
export interface PickTrade { a: string; b: string; aGets: readonly PickRef[]; bGets: readonly PickRef[] }

/**
 * Layer hypothetical trades over the real ownership map.
 *
 * Used by the mock draft, where "what if I move up" is half the question and the spreadsheet's
 * answer has always been to make another copy of the tab. Applied in order, so a pick can change
 * hands twice and the last trade wins — which is what a sequence of deals actually does.
 *
 * The base map is never mutated: a mock must not be able to move a real pick.
 */
export function applyPickTrades(
  base: ReadonlyMap<string, string>,
  trades: readonly PickTrade[],
): Map<string, string> {
  const out = new Map(base);
  for (const t of trades) {
    for (const p of t.aGets) out.set(`${p.round}:${p.seat}`, t.a);
    for (const p of t.bGets) out.set(`${p.round}:${p.seat}`, t.b);
  }
  return out;
}

/**
 * Drop declared keepers onto the picks they cost.
 *
 * `assignments` is what the declaration screen already computes: for each GM, the player and the
 * ROUND he is being paid with, collisions and traded picks resolved. Here that only has to be
 * matched to a concrete pick the GM owns in that round. Duplicates within a round are possible
 * (a pick-for-pick trade can leave a GM holding two), so they are consumed in board order.
 */
export function applyKeepers(
  board: readonly BoardPick[],
  assignments: Readonly<Record<string, ReadonlyArray<
    { player: string; round: number; pos?: string; team?: string; farm?: boolean }
  >>>,
): { board: BoardPick[]; errors: string[] } {
  const out = board.map((p) => ({ ...p }));
  const errors: string[] = [];

  for (const [gm, keepers] of Object.entries(assignments)) {
    for (const k of keepers) {
      const pick = out.find(
        (p) => p.round === k.round && !p.player
          && p.owner.toUpperCase() === gm.toUpperCase(),
      );
      if (!pick) {
        errors.push(
          `${gm} has no free Round ${k.round} pick to keep ${k.player} with — either it was `
          + 'traded away or another keeper already has it.',
        );
        continue;
      }
      pick.player = k.player;
      pick.pos = k.pos;
      pick.team = k.team;
      pick.keeper = true;
      if (k.farm) pick.farm = true;
    }
  }
  return { board: out, errors };
}

/** The first pick with nobody in it. Keepers count as filled, so the clock skips them. */
export function onTheClock(board: readonly BoardPick[]): BoardPick | null {
  return board.find((p) => !p.player) ?? null;
}

/** Picks still to be made, in order. Useful for "next up" and for the by-team view. */
export function remaining(board: readonly BoardPick[]): BoardPick[] {
  return board.filter((p) => !p.player);
}

/* ------------------------------------------------------------------------------------------ */

/**
 * Who may enter a given pick.
 *
 * Managers routinely miss draft night and hand their board to somebody else in the room — the
 * league has always allowed it informally, so the app models it explicitly rather than making
 * that person borrow a login. `designates[A] = B` reads "B drafts for A".
 *
 * A commissioner or admin can always type, because on draft night somebody has to be able to
 * fix a fat-fingered entry without a discussion about permissions.
 */
export interface DraftActor {
  gm: string;
  /** 'manager' | 'commissioner' | 'admin' — anything above manager can enter any pick. */
  privileged: boolean;
  designates?: Readonly<Record<string, string>>;
}

export function canEnter(pick: BoardPick, actor: DraftActor): boolean {
  if (actor.privileged) return true;
  const me = actor.gm.toUpperCase();
  if (pick.owner.toUpperCase() === me) return true;
  const proxy = actor.designates?.[pick.owner.toUpperCase()];
  return Boolean(proxy && proxy.toUpperCase() === me);
}

/** Every GM this actor is currently able to draft for, including himself. */
export function actingFor(actor: DraftActor, allGms: readonly string[]): string[] {
  if (actor.privileged) return [...allGms];
  const me = actor.gm.toUpperCase();
  return allGms.filter(
    (g) => g.toUpperCase() === me || actor.designates?.[g.toUpperCase()]?.toUpperCase() === me,
  );
}

/** "9.04" — round and pick-within-round, which is how the league writes it down. */
export function pickCode(pick: BoardPick, teams: number): string {
  const within = pick.round % 2 === 1 ? pick.slot : teams - pick.slot + 1;
  return `${pick.round}.${String(within).padStart(2, '0')}`;
}

/**
 * What goes in a cell the room agreed to skip.
 *
 * A passed pick is RECORDED rather than left empty, because "nobody has picked here yet" and "we
 * agreed to move on without him" are different facts and only one of them is finished. It is a
 * sentinel rather than a flag on the pick so that every existing reader — the board, the team
 * cards, the export, the archive — treats the cell as filled without any of them being changed.
 */
export const PASSED_PICK = '— passed —';

/** Was this cell skipped rather than drafted into? */
export const isPassed = (p: BoardPick): boolean => p.player === PASSED_PICK;

/* -- the lapsed clock -------------------------------------------------------------------------
 *
 * A pick clock that FORFEITS a turn is not what this league does, and a pick clock that stops the
 * whole draft while one man looks for his phone is not what it wants either. Tim's rule, which is
 * how the room has always actually worked:
 *
 *   When a clock runs out, THE DRAFT MOVES ON — the next manager may pick, on his own time.
 *   The man whose clock lapsed KEEPS HIS PICK and may still enter it whenever he gets back.
 *
 * So more than one pick is open at once, which the board has never modelled: `onTheClock` returns
 * exactly one and everything downstream assumed that. What follows replaces it for the live board
 * without disturbing the mock, which has no clocks at all.
 *
 * THE CONSEQUENCE THAT MATTERS is that two people can now be typing at the same time, and the
 * thing they are most likely to type is the same name — the best player left is obvious to both.
 * `guardPick` is the other half of this rule and is not optional.
 */

export interface OpenPick {
  pick: BoardPick;
  /** True when this pick's own clock has run out and the draft has moved past it. */
  lapsed: boolean;
  /**
   * The origin a countdown counts from — the instant the cell opened, pushed forward by
   * whatever the room has spent paused since. Not the same as "when it opened", and it should
   * not be: a clock that ran for ninety seconds either side of a ten-minute pizza break has
   * three minutes on it, not thirteen.
   */
  startedAt: number;
  /** Its allowance in seconds, or null where the round has no band configured. */
  seconds: number | null;
}

export interface OpenPicksInput {
  board: readonly BoardPick[];
  /** `pickSeconds` from `domain/clocks`, curried by round. Null means no clock in that round. */
  secondsFor: (round: number) => number | null;
  /** When the previous pick was made — where the first open clock counts from. */
  since: number;
  now: number;
  /**
   * How many picks may be in play at once. A lapsed pick holds its slot, so once this many have
   * run out the board is jammed and nothing new opens until one of them is entered.
   */
  max?: number;
  /** A commissioner's restart: the pick that has been waiting longest starts again here. */
  restartedAt?: number | null;
  /**
   * WHEN THE ROOM STOPPED, and when it started again.
   *
   * A pause is not a clock reset (Tim, 6 September: "pausing the draft caused all the pick
   * timers to reset"). The pizza arrives, the room argues about a rule, and nobody's time
   * should run while it does — but nobody's time should go back to the top either. So the
   * clocks count DRAFT time rather than wall time: every interval in here is subtracted from
   * the span between a pick opening and now. An interval with no `until` is a pause still on.
   */
  pauses?: readonly { at: number; until?: number | null }[];
}

/**
 * Every pick that may be entered right now.
 *
 * FOUR PICKS MAY BE IN PLAY AT ONCE, and the board is a queue of four slots rather than a
 * cascade of arithmetic. Tim, 6 September, watching four GMs run out of time at once:
 *
 *   "once Rajan's pick is in, that should allow the board to progress to Vishal's pick. The
 *    other lapsed picks stay red and hold their status, where they can be picked whenever …
 *    Once 4 all allow the timer to lapse, the board is logjammed and can't queue up the next
 *    picker until one of the 4 make a selection."
 *
 * So there are exactly two things that put a new pick in play — a clock running out, and a pick
 * being made — and both are subject to the four-slot cap. A pick that has lapsed does NOT leave
 * the window: it stays open, red, enterable, and holding its slot. That is what makes the jam a
 * jam, and it is the rule that makes "he keeps his pick" cost something.
 *
 * A PICK'S CLOCK STARTS WHEN IT GOES IN THE WINDOW AND NEVER RESTARTS. That is the property
 * three separate bugs came from, each an attempt to derive the start from the board's CURRENT
 * shape: from the first empty cell, from the newest timestamp, from the furthest pick made. All
 * three move when a delinquent pick is finally entered, and the room watched an hour of elapsed
 * time reset to zero because somebody filled in a pick from twenty minutes ago.
 *
 * The start cannot be read off the board's shape, because it is a fact about WHEN, so this
 * replays the night instead. Every input is data every client already has — the made picks with
 * their timestamps, the round's allowance, the commissioner's restart — so ten browsers replay
 * the same night and land on the same four clocks, with no turn state to drift and nothing to
 * store. It is a discrete-event walk over at most a few hundred instants; the cost is nothing
 * and the property is exact.
 */
export function openPicks(
  { board, secondsFor, since, now, max = 4, restartedAt = null, pauses = [] }: OpenPicksInput,
): OpenPick[] {
  const cells = [...board].sort((a, b) => a.overall - b.overall);
  const byOverall = new Map(cells.map((p) => [p.overall, p]));

  /* -- draft time ---------------------------------------------------------------------------
   *
   * Everything below counts in draft time: wall time with the pauses taken out. Two helpers do
   * the whole job, and both are pure functions of the ledger, so ten browsers agree.
   */
  const stops = [...pauses]
    .map((p) => ({ at: p.at, until: p.until ?? Infinity }))
    .filter((p) => Number.isFinite(p.at))
    .sort((a, b) => a.at - b.at);
  /** How much of the wall-clock span [a, b] the room spent stopped. */
  const stopped = (a: number, b: number): number => {
    if (b <= a || stops.length === 0) return 0;
    let n = 0;
    for (const s_ of stops) {
      const lo = Math.max(a, s_.at);
      const hi = Math.min(b, s_.until);
      if (hi > lo) n += hi - lo;
    }
    return n;
  };
  /**
   * The wall-clock instant at which a clock started at `from` will have burned `ms` of draft
   * time. Walked rather than solved: run until the next stop, jump it, run again. Exact for any
   * number of pauses, and `Infinity` while one is still open, which is what "the room has not
   * started again" means to a countdown.
   */
  const after = (from: number, ms: number): number => {
    let x = from;
    let left = ms;
    for (const s_ of stops) {
      if (s_.until <= x) continue;
      const running = Math.max(0, s_.at - x);
      if (left <= running) return x + left;
      left -= running;
      if (!Number.isFinite(s_.until)) return Infinity;
      x = s_.until;
    }
    return x + left;
  };

  /*
   * When each filled cell stopped being open. A KEEPER WAS NEVER ON ANYBODY'S CLOCK — he
   * arrives pre-placed and nobody chose him at 8pm — so he counts as filled from before the
   * beginning, which keeps a kept player in round fourteen from declaring the draft over.
   */
  const filledAt = new Map<number, number>();
  for (const p of cells) {
    if (!p.player) continue;
    const t = p.madeAt ? Date.parse(p.madeAt) : NaN;
    filledAt.set(p.overall, Number.isNaN(t) ? -Infinity : t);
  }
  const filledBy = (overall: number, t: number) => {
    const at = filledAt.get(overall);
    return at !== undefined && at <= t;
  };

  /** overall -> the instant it went in the window. Written once each, never rewritten. */
  const opened = new Map<number, number>();
  const openCount = (t: number) => {
    let n = 0;
    for (const [o] of opened) if (!filledBy(o, t)) n += 1;
    return n;
  };
  /**
   * Is a clock actually TICKING at `t` — an open pick that has not been filled and has not run
   * out? A pick on a round with no band never lapses, so it counts as running until it is made.
   */
  const runningAt = (t: number) => {
    for (const [o] of opened) {
      if (filledBy(o, t)) continue;
      const seconds = secondsFor(byOverall.get(o)!.round);
      if (seconds === null) return true;
      if (after(opened.get(o)!, seconds * 1000) > t) return true;
    }
    return false;
  };
  /**
   * One vacancy, one pick. Never two at once, or a board with no clocks would open four.
   *
   * AND NEVER WHILE SOMEBODY'S CLOCK IS STILL RUNNING (Tim, 8 September: "we didn't get a full
   * clock though.. I only got 2 seconds before my pick switched to lapsed").
   *
   * "Four picks can be in play at any time" means four picks may be ENTERED at any time — three
   * men who let their clocks run out, plus the man who is up. It never meant four clocks running
   * side by side. Without this guard, entering a delinquent pick freed a slot and started the
   * NEXT man's band immediately, alongside the band already running: two clocks counting the same
   * two minutes, and the second man's turn expiring seconds after the first. He was on the clock
   * from a moment nobody told him about, and the board showed him as up for the two seconds
   * between the two expiries.
   *
   * So a vacancy is not enough. The queue moves when the room is waiting on nobody: the running
   * pick is made, or it runs out. A lapsed pick still holds its slot, four lapsed is still a jam,
   * and filling one out of order still frees a slot — it just no longer jumps the queue.
   */
  const step = (t: number) => {
    if (openCount(t) >= max) return;
    if (runningAt(t)) return;
    const next = cells.find((p) => !opened.has(p.overall) && !filledBy(p.overall, t));
    if (next) opened.set(next.overall, t);
  };

  const stamps = [...filledAt.values()].filter((n) => Number.isFinite(n));
  let t = stamps.length ? Math.min(...stamps, since) : since;
  step(t);

  /* Walk instant to instant: a clock running out, a pick landing, a commissioner's restart. */
  for (let guard = 0; guard < 4000; guard += 1) {
    let next = Infinity;
    for (const [o, at] of opened) {
      if (filledBy(o, t)) continue;
      const seconds = secondsFor(byOverall.get(o)!.round);
      if (seconds === null) continue;
      const lapse = after(at, seconds * 1000);
      if (lapse > t && lapse < next) next = lapse;
    }
    for (const m of stamps) if (m > t && m < next) next = m;
    if (restartedAt !== null && restartedAt > t && restartedAt < next) next = restartedAt;
    if (!Number.isFinite(next) || next > now) break;
    t = next;

    /*
     * WHAT HAPPENED AT THIS INSTANT, worked out before anything is changed. A clock running out
     * and a pick landing both free a slot, so both queue the next man up. A restart does not:
     * it hands time back to somebody already in the window, and the queue is where it was.
     */
    const lapseHere = [...opened].some(([o, at]) => {
      if (filledBy(o, t)) return false;
      const seconds = secondsFor(byOverall.get(o)!.round);
      return seconds !== null && after(at, seconds * 1000) === t;
    });
    const madeHere = stamps.some((m) => m === t);

    /*
     * A RESTART GIVES BACK THE TIME OF THE PICK AT THE FRONT OF THE WINDOW (Tim, 6 September:
     * "the 'restart the clock' option should restart the clock of the last active or lapsed
     * pick — not reset the first in that series").
     *
     * With one pick open that is the man on the clock, which is what `Restart pick` has always
     * meant. With four open it is the newest of them: the room is waiting on the front of the
     * queue to move, and handing the time back to the oldest red cell restarts a clock that ran
     * out an hour ago while leaving the board exactly as stuck as it was.
     */
    if (restartedAt !== null && t === restartedAt) {
      let front: BoardPick | null = null;
      for (const p of cells) {
        if (opened.has(p.overall) && !filledBy(p.overall, t)) front = p;
      }
      if (front) opened.set(front.overall, t);
    }
    if (lapseHere || madeHere) step(t);
  }

  const out: OpenPick[] = [];
  for (const p of cells) {
    const at = opened.get(p.overall);
    if (at === undefined || filledBy(p.overall, now)) continue;
    const seconds = secondsFor(p.round);
    /*
     * `startedAt` IS AN ORIGIN TO COUNT FROM, not the instant the cell opened — the two differ
     * by whatever the room spent stopped since. Shifting it here means every clock on the
     * screen counts draft time without knowing the pause ledger exists.
     */
    const held = stopped(at, now);
    out.push({
      pick: p,
      lapsed: seconds !== null && now - at - held >= seconds * 1000,
      startedAt: at + held,
      seconds,
    });
  }
  return out;
}

/* ------------------------------------------------------------------------------------------ */

export interface RoomInput {
  /** The board with its saved players and `madeAt` stamps already applied. */
  board: readonly BoardPick[];
  status: 'setup' | 'live' | 'complete' | null | undefined;
  /** `pickSeconds` from `domain/clocks`, curried by round. */
  secondsFor: (round: number) => number | null;
  /** A commissioner's restart: the front of the window starts again here, and nothing else moves. */
  restartedAt?: string | null;
  /** When the room was told the draft was live — the origin for a board with no pick on it yet. */
  startedAt?: string | null;
  /** The draft doc's own stamp, for a board that went live before `startedAt` existed. */
  updatedAt?: string | null;
  pauses?: readonly { at: string; until?: string | null }[];
  now: number;
}

export interface Room {
  /** The man the room is actually waiting on, or null when every open pick has run out. */
  clock: BoardPick | null;
  /** Every pick that may be entered right now, in board order. */
  open: OpenPick[];
  /** Those of them whose clocks have run out. */
  lapsed: OpenPick[];
  /** All four slots open and all four late: nothing new opens until one is entered. */
  jammed: boolean;
  /** Where the room is as an instant — the moment the furthest pick down the board was made. */
  since: string | null;
}

/**
 * WHO IS ON THE CLOCK — one answer, for every screen that asks.
 *
 * `onTheClock` returns the FIRST unmade pick, which was right while only one could be open. Once
 * a clock lapses the first unmade pick is the man who is late and the man genuinely up is
 * further down the board, so the two readings diverge — and they diverged in different places:
 * the draft board learned the frontier reading in 17a and Home and the shell strip did not. On
 * Tim's screen of 8 September the board said `1.03 Ryan` while Home said `JOHN is on the clock`
 * about a pick two lapses old.
 *
 * So the reading lives here now and the screens read it, rather than each deciding for itself.
 *
 * The origin is the furthest pick MADE, not the newest stamp: entering a delinquent GM's pick
 * puts `now` on a cell whose window closed minutes ago, and taking the newest stamp made that
 * the origin of every clock on the board. See `openPicks`.
 */
export function whoIsUp(
  { board, status, secondsFor, restartedAt = null, startedAt = null, updatedAt = null,
    pauses = [], now }: RoomInput,
): Room {
  const first = board.find((p) => !p.player) ?? null;
  const off: Room = { clock: first, open: [], lapsed: [], jammed: false, since: null };
  if (status !== 'live') return off;

  let furthest: { overall: number; at: string } | null = null;
  for (const p of board) {
    if (!p.player || !p.madeAt) continue;
    if (!furthest || p.overall > furthest.overall) furthest = { overall: p.overall, at: p.madeAt };
  }
  /*
   * THE RESTART IS NOT THE ORIGIN. It is a separate input to `openPicks`, which hands the time
   * back to the front of the window and moves nothing else; taking it as the origin as well
   * restarts the whole cascade from the first unmade cell and wipes every elapsed figure behind
   * it. See `sinceLastPick` in `DraftBoard` for the night this cost.
   */
  const since = furthest?.at ?? startedAt ?? restartedAt ?? updatedAt ?? null;
  if (!since) return off;

  const open = openPicks({
    board,
    secondsFor,
    since: Date.parse(since),
    now,
    restartedAt: restartedAt ? Date.parse(restartedAt) : null,
    pauses: pauses.map((x) => ({ at: Date.parse(x.at), until: x.until ? Date.parse(x.until) : null })),
  });
  const lapsed = open.filter((o) => o.lapsed);
  return {
    clock: open.find((o) => !o.lapsed)?.pick ?? first,
    open,
    lapsed,
    jammed: open.length > 0 && lapsed.length === open.length,
    since,
  };
}

/**
 * Whether a pick may still be entered, given everything on the board right now.
 *
 * THE RACE THIS EXISTS FOR: with two picks open, two people can send the same player within the
 * same second. Firestore's last-write-wins would take both, and the board would show one man
 * drafted twice — discovered an hour later, with eight picks made on top of it.
 *
 * The mitigation is the same two-part one the farm auction uses: re-read immediately before
 * writing, then ask here whether the write is still legal. That narrows the race to one round
 * trip rather than closing it, so the guard is written to make the SURVIVING outcome the correct
 * one: the pick that is already recorded wins, and the second one loses loudly with the name of
 * who took him. A man told "Amit just took him" retypes in four seconds. A silent duplicate costs
 * the round.
 */
export function guardPick(
  board: readonly BoardPick[],
  overall: number,
  player: string,
): Guard {
  const target = board.find((p) => p.overall === overall);
  if (!target) return { ok: false, why: 'That pick is not on this board.' };
  if (target.player) {
    return { ok: false, why: `${target.player} is already in at ${target.round}.${target.seat}.` };
  }

  const wanted = player.trim().toLowerCase();
  if (!wanted) return { ok: false, why: 'No player named.' };

  const taken = board.find((p) => p.player && p.player.trim().toLowerCase() === wanted);
  if (taken) {
    return {
      ok: false,
      why: `${taken.player} has gone — ${taken.owner} took him at ${taken.round}.${taken.seat}.`,
    };
  }
  return { ok: true };
}

export type Guard = { ok: true } | { ok: false; why: string };
